import { useState, useRef, useEffect, useLayoutEffect, useCallback, memo, useMemo } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  AppBar,
  Box,
  CssBaseline,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Menu,
  MenuItem,
} from '@mui/material';
import {
  Menu as MenuIcon,
  ChevronLeft,
  LocationOn,
  Storage,
  People,
  Inventory,
  Category,
  ShoppingCart,
  Logout,
  Home,
  Assessment,
  SmartToy,
  SwapHoriz,
  CompareArrows,
  Settings,
  LocalShipping,
  Lock,
  Folder,
  SpaceDashboard,
  ErrorOutline,
  BugReport,
  TableChart,
  AdminPanelSettings,
  UploadFile,
  AssignmentTurnedIn,
  Checklist,
  Warehouse,
  AltRoute,
  Inventory2,
  Analytics,
  PendingActions,
  Map,
  Extension,
  MonitorHeart,
} from '@mui/icons-material';
import jyBlackLogo from '../assets/JY BLACK.png';
import jyWhiteLogo from '../assets/JY WHITE.png';
import PasswordChangeDialog from './PasswordChangeDialog';

const defaultDrawerWidth = 280;
const minDrawerWidth = 200;
const maxDrawerWidth = 500;

const SIDEBAR = {
  bg: '#FFFFFF',
  headerBg: '#FFFFFF',
  border: '#E2E8F0',
  text: '#000000',
  textMuted: '#000000',
  textInactive: '#000000',
  selectedBg: '#FFFFFF',
  selectedShadow: '0 1px 3px rgba(45, 55, 72, 0.08), 0 1px 2px rgba(45, 55, 72, 0.04)',
  hoverBg: 'rgba(0, 0, 0, 0.05)',
  groupLabel: '#000000',
};

// Icon components - memoized to prevent recreation
const MenuIcons = {
  Home,
  Inventory,
  Category,
  LocationOn,
  Storage,
  ShoppingCart,
  LocalShipping,
  SpaceDashboard,
  Assessment,
  SwapHoriz,
  People,
  SmartToy,
  Folder,
  CompareArrows,
  ErrorOutline,
  BugReport,
  TableChart,
  AdminPanelSettings,
  UploadFile,
  AssignmentTurnedIn,
  Checklist,
  Warehouse,
  AltRoute,
  Inventory2,
  Analytics,
  PendingActions,
  Map,
  Extension,
  MonitorHeart,
};

// Updated menu items with grouping structure - using icon keys instead of JSX
const menuItems = [
  // Standalone Dashboard
  { text: 'Dashboard', iconKey: 'Home', path: '/dashboard', standalone: true },
  { text: 'Live System Health', iconKey: 'MonitorHeart', path: '/system-health', standalone: true },

  // Transactions Group
  { text: 'Sales Orders', iconKey: 'ShoppingCart', path: '/sales-order-list', group: 'transactions' },
  { text: 'Ship Confirm', iconKey: 'AssignmentTurnedIn', path: '/ship-confirm-by-status', group: 'regular' },
  { text: 'Pick Progress Dashboard', iconKey: 'Checklist', path: '/regular-pick-progress', group: 'regular' },
  { text: 'Amazon AMXL Dashboard', iconKey: 'Warehouse', path: '/amazon-amxl-dashboard', group: 'regular' },
  { text: 'ATS Dashboard', iconKey: 'AltRoute', path: '/ats-dashboard', group: 'regular' },
  { text: 'UPS Dashboard', iconKey: 'Inventory2', path: '/ups-dashboard', group: 'regular' },

  // LTL Dashboard under Transactions
  { text: 'LTL Dashboard', iconKey: 'Analytics', path: '/ltl-dashboard', group: 'ltl' },
  { text: 'LTL Pick Progress', iconKey: 'PendingActions', path: '/ltl-pick-progress', group: 'ltl' },
  { text: 'LTL Pallets', iconKey: 'Map', path: '/ltl-pallet-map', group: 'ltl' },


  // Parts Group
  { text: 'Parts Dashboard', iconKey: 'Extension', path: '/parts-dashboard', group: 'parts' },
  //{ text: 'Parts Reports', iconKey: 'Assessment', path: '/parts-reports', group: 'parts' },

  // Bin Operations Group (Reports)
  { text: 'User Pick Status', iconKey: 'SpaceDashboard', path: '/user-pick-status', group: 'bin-operations' },
  { text: 'Item Sales Report', iconKey: 'Assessment', path: '/item-sales-report', group: 'bin-operations' },
  { text: 'No Movement Report', iconKey: 'LocalShipping', path: '/ship-tracking', group: 'bin-operations' },

  // User Management Group
  { text: 'Web Portal User', iconKey: 'People', path: '/admins', group: 'user-management', alwaysVisible: true },
  { text: 'WMS Mobile Users', iconKey: 'SmartToy', path: '/wms-ai-users', group: 'user-management', alwaysVisible: true },
  { text: 'Roles', iconKey: 'AdminPanelSettings', path: '/roles', group: 'user-management', alwaysVisible: true },
  { text: 'Fedex Invoices', iconKey: 'Folder', path: '/fedex-files', group: 'files', alwaysVisible: false },
  { text: 'Fedex Recommended Cost', iconKey: 'Assessment', path: '/fedex-shipping-analysis', group: 'pricing' },
  { text: 'Marketplace Recommended', iconKey: 'CompareArrows', path: '/price-comparison', group: 'pricing' },
  { text: 'Marketplace Price Update', iconKey: 'UploadFile', path: '/marketplace-price-update', group: 'pricing' },

  // Scrape Data Group
  { text: 'Scraper', iconKey: 'TableChart', path: '/all-scraper-itemdata', group: 'scrape-data' },
];

// Group configuration - using icon keys
const groupConfig = {
  'master-data': { title: 'Master Data', iconKey: 'Category', defaultOpen: false },
  transactions: { title: 'Transactions', iconKey: 'LocalShipping', defaultOpen: false },
  regular: { title: 'Regular Dashboard', iconKey: 'SpaceDashboard', defaultOpen: false },
  ltl: { title: 'LTL Dashboard', iconKey: 'SpaceDashboard', defaultOpen: false },
  parts: { title: 'Parts', iconKey: 'Inventory', defaultOpen: false },
  'bin-operations': { title: 'Reports', iconKey: 'Assessment', defaultOpen: false },
  'user-management': { title: 'User Management', iconKey: 'People', defaultOpen: false },
  files: { title: 'Upload Files', iconKey: 'Folder', defaultOpen: false },
  pricing: { title: 'Pricing', iconKey: 'Assessment', defaultOpen: false },
  'scrape-data': { title: 'Scraper Data', iconKey: 'TableChart', defaultOpen: false },
};

// Function to map menu item text to feature keys
const getFeatureKey = (text) => {
  const featureMap = {
    'Locations': 'locations',
    'Bins': 'bins',
    'Inventory': 'inventory',
    'Items': 'items',
    'LTL Sales Orders': 'salesorder',
    'Regular Pick Sales Orders': 'dropshipSalesOrders',
    'Sales Orders': 'salesOrderList',
    'LTL Dashboard': 'ltlDashboard',
    'LTL Pick Progress': 'ltlPickProgress',
    'LTL Pallets': 'ltlDashboard',
    'LTL Pallet Map': 'ltlDashboard',
    'Pick Progress Dashboard': 'regularPickProgress',
    'Fedex Invoices': 'fedexFileUpload',
    'Fedex Recommended Cost': 'fedexShippingAnalysis',
    'Marketplace Recommended': 'marketPlacePrice',
    'Ship Confirm': 'regularDashboard',
    'No Movement Report': 'regularDashboard',
    'Ship Tracking': 'regularDashboard',
    'Amazon AMXL Dashboard': 'amAmxlDashboard',
    'ATS Dashboard': 'amAtsDashboard',
    'UPS Dashboard': 'amUpsDashboard',
    'User Pick Status': 'userPickStatus',
    'Item Sales Report': 'itemSalesByCus',
    'Parts Dashboard': 'PartsDashboard',
    'Parts Reports': 'PartsDashboard',
    'Marketplace Price Update': 'UpdateMarketplacePricing',
    'Scraper': 'masterScraperData'
  };
  return featureMap[text];
};

// Memoized menu item component - using Link for better performance
const MenuItemButton = memo(({ item, selected, onNavigate }) => {
  const IconComponent = MenuIcons[item.iconKey];

  const handleClick = useCallback(() => {
    if (onNavigate) {
      onNavigate();
    }
  }, [onNavigate]);

  return (
    <ListItemButton
      component={Link}
      to={item.path}
      selected={selected}
      onClick={handleClick}
      sx={{
        py: 1.1,
        px: 1.5,
        mx: 0.5,
        borderRadius: '10px',
        mb: 0.25,
        textDecoration: 'none',
        color: selected ? SIDEBAR.text : SIDEBAR.textInactive,
        backgroundColor: selected ? SIDEBAR.selectedBg : 'transparent',
        boxShadow: selected ? SIDEBAR.selectedShadow : 'none',
        transition: 'background-color 0.15s ease, box-shadow 0.15s ease',
        '&.Mui-selected': {
          backgroundColor: SIDEBAR.selectedBg,
          color: SIDEBAR.text,
          boxShadow: SIDEBAR.selectedShadow,
          '&:hover': {
            backgroundColor: SIDEBAR.selectedBg,
          },
          '& .MuiListItemIcon-root': {
            color: SIDEBAR.text,
          },
          '& .MuiListItemText-primary': {
            color: SIDEBAR.text,
            fontWeight: 600,
            fontSize: '0.9375rem',
          },
        },
        '&:hover': {
          backgroundColor: selected ? SIDEBAR.selectedBg : SIDEBAR.hoverBg,
        },
        '& .MuiListItemIcon-root': {
          minWidth: 36,
          color: selected ? SIDEBAR.text : SIDEBAR.textMuted,
        },
        '& .MuiListItemText-primary': {
          fontSize: '0.9375rem',
          fontWeight: selected ? 600 : 500,
          color: selected ? SIDEBAR.text : SIDEBAR.textInactive,
        },
      }}
    >
      <ListItemIcon sx={{ '& .MuiSvgIcon-root': { fontSize: '1.25rem' } }}>
        {IconComponent && <IconComponent />}
      </ListItemIcon>
      <ListItemText primary={item.text} />
    </ListItemButton>
  );
}, (prevProps, nextProps) => {
  return prevProps.selected === nextProps.selected && prevProps.item.path === nextProps.item.path;
});

// Module-level variable to preserve sidebar scroll position across route transitions
let savedSidebarScrollTop = 0;

// Memoize the drawer content
const DrawerContent = memo(({ drawerOpen, location, onMobileNavigate }) => {
  const selectedPath = location.pathname;
  const navRef = useRef(null);

  useLayoutEffect(() => {
    if (navRef.current) {
      navRef.current.scrollTop = savedSidebarScrollTop;
    }
  }, [location.pathname]);

  const handleNavScroll = useCallback((e) => {
    if (e.target && typeof e.target.scrollTop === 'number') {
      savedSidebarScrollTop = e.target.scrollTop;
    }
  }, []);

  // Check isSuperAdmin - use state to make it reactive
  const [isSuperAdmin, setIsSuperAdmin] = useState(() => {
    return sessionStorage.getItem('isSuperAdmin') === 'true';
  });

  // Update isSuperAdmin when sessionStorage changes
  useEffect(() => {
    const checkSuperAdmin = () => {
      setIsSuperAdmin(sessionStorage.getItem('isSuperAdmin') === 'true');
    };

    checkSuperAdmin();

    const handleStorageChange = (e) => {
      if (e.key === 'isSuperAdmin') {
        checkSuperAdmin();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    const interval = setInterval(checkSuperAdmin, 1000);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, []);

  const { features } = useAuth();

  const featuresString = useMemo(() => {
    return features ? JSON.stringify(features) : '';
  }, [features]);

  /**
   * Data-driven nav sections from menuItems + groupConfig.
   * Shape: [{ id, title, items: [{ text, path, iconKey, ... }] }]
   */
  const navSections = useMemo(() => {
    const standalone = [];
    const groups = {};

    menuItems.forEach((item) => {
      if (item.group === 'user-management' && !isSuperAdmin) return;
      if (item.text === 'Submitted Pallet List' && !isSuperAdmin) return;
      if (item.text === 'AMXL Pick Progress' && !isSuperAdmin) return;

      const superAdminOnlyItems = [
        'AMXL Pick Progress',
        'Submitted Pallet List',
        'FTZ-Container',
      ];
      const isSuperAdminOnlyItem = superAdminOnlyItems.includes(item.text);

      if (features && typeof features === 'object' && !isSuperAdmin && !isSuperAdminOnlyItem) {
        const featureKey = getFeatureKey(item.text);
        if (featureKey && !item.alwaysVisible) {
          const hasAccess =
            features[featureKey] === true ||
            (featureKey === 'UpdateMarketplacePricing' && features['marketplacePriceUpdate'] === true);
          if (!hasAccess) {
            return;
          }
        }
      }

      if (item.standalone) {
        standalone.push(item);
      } else if (item.group) {
        if (!groups[item.group]) groups[item.group] = [];
        groups[item.group].push(item);
      }
    });

    const sections = [];

    if (standalone.length > 0) {
      sections.push({ id: 'standalone', title: null, items: standalone });
    }

    Object.keys(groupConfig).forEach((groupKey) => {
      const items = groups[groupKey];
      if (items && items.length > 0) {
        sections.push({
          id: groupKey,
          title: groupConfig[groupKey].title,
          items,
        });
      }
    });

    return sections;
  }, [isSuperAdmin, featuresString]);

  return (
    <Box
      role="navigation"
      aria-label="Main navigation"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        backgroundColor: SIDEBAR.bg,
        overflow: 'hidden',
      }}
    >
      {/* Static brand header */}
      {drawerOpen && (
        <Box
          className="sidebar-brand"
          sx={{
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            px: 2.25,
            pt: 2,
            pb: 1.5,
            backgroundColor: SIDEBAR.headerBg,
          }}
        >
          <Box
            component="img"
            src={jyBlackLogo}
            alt="JY Logo"
            sx={{
              height: 40,
              width: 'auto',
              display: 'block',
              flexShrink: 0,
              objectFit: 'contain',
            }}
          />
          <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: '1.125rem',
                fontWeight: 700,
                color: SIDEBAR.text,
                lineHeight: 1.15,
                letterSpacing: '-0.01em',
              }}
            >
              JYSWMS
            </Typography>
            <Typography
              sx={{
                fontSize: '0.75rem',
                fontWeight: 500,
                color: SIDEBAR.textMuted,
                lineHeight: 1.3,
                mt: 0.35,
              }}
            >
              Warehouse Admin
            </Typography>
          </Box>
        </Box>
      )}

      {/* Scrollable nav body — config-driven sections */}
      <Box
        className="single-nav"
        ref={navRef}
        onScroll={handleNavScroll}
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          overflowX: 'hidden',
          py: 1.5,
          px: 0.75,
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          '&::-webkit-scrollbar': {
            display: 'none',
          },
        }}
      >
        {navSections.map((section) => (
          <Box key={section.id} className="nav-section" sx={{ mb: section.title ? 1.5 : 0.75 }}>
            {section.title && (
              <Typography
                className="nav-section-title"
                component="div"
                sx={{
                  px: 1.75,
                  pt: 1,
                  pb: 0.75,
                  fontSize: '0.6875rem',
                  fontWeight: 700,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  color: SIDEBAR.groupLabel,
                  userSelect: 'none',
                }}
              >
                {section.title}
              </Typography>
            )}
            <List component="nav" disablePadding>
              {section.items.map((item) => {
                const isSelected =
                  selectedPath === item.path ||
                  (item.path !== '/dashboard' && selectedPath.startsWith(`${item.path}/`));
                return (
                  <ListItem key={item.path} disablePadding sx={{ mb: 0.25 }}>
                    <MenuItemButton
                      item={item}
                      selected={isSelected}
                      onNavigate={onMobileNavigate}
                    />
                  </ListItem>
                );
              })}
            </List>
          </Box>
        ))}
      </Box>
    </Box>
  );
}, (prevProps, nextProps) => {
  return (
    prevProps.location.pathname === nextProps.location.pathname &&
    prevProps.drawerOpen === nextProps.drawerOpen &&
    prevProps.onMobileNavigate === nextProps.onMobileNavigate
  );
});

const DashboardLayout = memo(({ children, disablePadding = false }) => {
  const [drawerOpen, setDrawerOpen] = useState(true);
  const [drawerWidth, setDrawerWidth] = useState(defaultDrawerWidth);
  const [isResizing, setIsResizing] = useState(false);
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const [settingsAnchorEl, setSettingsAnchorEl] = useState(null);
  const [isSettingsRotating, setIsSettingsRotating] = useState(false);
  const [passwordChangeDialogOpen, setPasswordChangeDialogOpen] = useState(false);
  const { logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const mainContentRef = useRef(null);
  const resizeRef = useRef(null);
  const handleDrawerToggle = useCallback(() => {
    setDrawerOpen((prev) => !prev);
  }, []);

  // Close mobile drawer on navigation
  const handleMobileNavigate = useCallback(() => {
    if (window.innerWidth < 600) {
      setDrawerOpen(false);
    }
  }, []);

  const handleLogoutClick = useCallback(() => {
    setLogoutDialogOpen(true);
  }, []);

  const handleLogoutConfirm = useCallback(() => {
    setLogoutDialogOpen(false);
    logout();
  }, [logout]);

  const handleLogoutCancel = useCallback(() => {
    setLogoutDialogOpen(false);
  }, []);

  const handleSettingsClick = useCallback((event) => {
    const anchorElement = event.currentTarget;
    setIsSettingsRotating(true);
    // Open dropdown after rotation animation completes (300ms)
    setTimeout(() => {
      setSettingsAnchorEl(anchorElement);
    }, 300);
  }, []);

  const handleSettingsClose = useCallback(() => {
    setSettingsAnchorEl(null);
    setIsSettingsRotating(false);
  }, []);

  const handleNetsuiteSetup = useCallback(() => {
    handleSettingsClose();
    navigate('/netsuite-setup');
  }, [handleSettingsClose, navigate]);

  const handlePasswordChange = useCallback(() => {
    handleSettingsClose();
    setPasswordChangeDialogOpen(true);
  }, [handleSettingsClose]);

  // Resize handlers
  const handleResizeStart = useCallback((e) => {
    e.preventDefault();
    setIsResizing(true);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, []);

  const handleResizeMove = useCallback((e) => {
    if (!isResizing) return;

    const newWidth = e.clientX;
    if (newWidth >= minDrawerWidth && newWidth <= maxDrawerWidth) {
      setDrawerWidth(newWidth);
    }
  }, [isResizing]);

  const handleResizeEnd = useCallback(() => {
    setIsResizing(false);
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  }, []);

  // Add global mouse event listeners for resize
  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleResizeMove);
      document.addEventListener('mouseup', handleResizeEnd);

      return () => {
        document.removeEventListener('mousemove', handleResizeMove);
        document.removeEventListener('mouseup', handleResizeEnd);
      };
    }
  }, [isResizing, handleResizeMove, handleResizeEnd]);

  const drawerStyles = useMemo(() => ({
    display: { xs: 'none', sm: drawerOpen ? 'block' : 'none' },
    transition: 'none !important',
    '& .MuiDrawer-paper': {
      boxSizing: 'border-box',
      width: drawerWidth,
      height: '100vh',
      borderRight: `1px solid ${SIDEBAR.border}`,
      backgroundColor: SIDEBAR.bg,
      position: 'relative',
      top: 0,
      left: 0,
      boxShadow: 'none',
      overflowX: 'hidden',
      overflowY: 'hidden',
      display: 'flex',
      flexDirection: 'column',
    },
  }), [drawerOpen, drawerWidth]);

  const mainStyles = useMemo(() => ({
    flexGrow: 1,
    p: disablePadding ? 0 : { xs: 1, sm: 1 },
    width: { xs: '100%', sm: drawerOpen ? `calc(100% - ${drawerWidth}px)` : '100%' },
    height: { xs: 'calc(100vh - 40px)', sm: 'calc(100vh - 48px)' },
    overflow: 'hidden',
    position: 'relative',
    mt: { xs: '40px', sm: '48px' },
    pt: 0,
    outline: 'none',
    display: 'flex',
    flexDirection: 'column',
    boxSizing: 'border-box',
    minHeight: 0,
  }), [drawerOpen, drawerWidth, disablePadding]);

  // Focus management for accessibility
  useEffect(() => {
    if (!drawerOpen && mainContentRef.current) {
      mainContentRef.current.focus();
    }
  }, [drawerOpen]);

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          width: { xs: '100%', sm: drawerOpen ? `calc(100% - ${drawerWidth}px)` : '100%' },
          ml: { xs: 0, sm: drawerOpen ? `${drawerWidth}px` : 0 },
          backgroundColor: 'primary.main',
          borderRadius: 0,
          boxShadow: 'none',
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
      >
        <Toolbar sx={{
          position: 'relative',
          minHeight: { xs: '40px', sm: '48px' },
          py: { xs: 0.5, sm: 0.75 },
          px: { xs: 1, sm: 2 }
        }}>
          <IconButton
            color="inherit"
            aria-label={drawerOpen ? 'close drawer' : 'open drawer'}
            edge="start"
            onClick={handleDrawerToggle}
            sx={{
              mr: 1.5,
              color: 'common.white',
              display: { xs: 'flex', sm: 'flex' },
              padding: { xs: '4px', sm: '6px' },
              '&:hover': {
                background: 'none !important',
              }
            }}
          >
            {drawerOpen ? (
              <ChevronLeft sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }} />
            ) : (
              <MenuIcon sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }} />
            )}
          </IconButton>
          {!drawerOpen && (
            <img
              src={jyWhiteLogo}
              alt="JY Logo"
              style={{
                height: '32px',
                width: 'auto',
                marginRight: 12,
                display: { xs: 'none', sm: 'block' }
              }}
            />
          )}
          <Typography
            variant="h6"
            noWrap
            component="div"
            sx={{
              color: 'common.white',
              display: 'flex',
              alignItems: 'center',
              fontSize: { xs: '0.875rem', sm: '1rem' },
              fontWeight: 500
            }}
          >
            JYSWMS Admin Portal
          </Typography>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            position: 'absolute',
            right: { xs: 16, sm: 24 },
            top: '50%',
            transform: 'translateY(-50%)'
          }}>
            <Typography
              variant="body1"
              noWrap
              sx={{
                color: 'common.white',
                mr: 2,
                display: { xs: 'none', sm: 'block' }
              }}
            >
              Hi, {sessionStorage.getItem('username')}
            </Typography>
            <IconButton
              color="inherit"
              aria-label="settings"
              onClick={handleSettingsClick}
              sx={{
                '&:hover': { color: 'white' },
                transform: isSettingsRotating ? 'rotate(90deg)' : 'rotate(0deg)',
                transition: 'transform 0.3s ease-in-out',
                mr: 1
              }}
            >
              <Settings />
            </IconButton>
            <IconButton
              color="inherit"
              aria-label="logout"
              onClick={handleLogoutClick}
              sx={{
                '&:hover': {
                  color: 'error.light',
                  backgroundColor: 'rgba(211, 47, 47, 0.08)'
                },
                transition: 'all 0.2s ease-in-out'
              }}
            >
              <Logout />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{
          width: { sm: drawerOpen ? drawerWidth : 0 },
          flexShrink: { sm: 0 },
        }}
      >
        <Drawer
          variant="temporary"
          open={!drawerOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: false,
            disableEnforceFocus: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              height: 'calc(100vh - 56px)',
              borderRight: `1px solid ${SIDEBAR.border}`,
              backgroundColor: SIDEBAR.bg,
              zIndex: (theme) => theme.zIndex.appBar + 1,
              mt: '56px',
              boxShadow: 'none',
              overflowY: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            },
          }}
        >
          <DrawerContent
            drawerOpen={drawerOpen}
            location={location}
            onMobileNavigate={handleMobileNavigate}
          />
        </Drawer>
        <Drawer
          variant="persistent"
          sx={drawerStyles}
          open={drawerOpen}
        >
          <DrawerContent
            drawerOpen={drawerOpen}
            location={location}
            onMobileNavigate={handleMobileNavigate}
          />
          {/* Resize Handle */}
          <Box
            ref={resizeRef}
            onMouseDown={handleResizeStart}
            sx={{
              position: 'absolute',
              right: 0,
              top: 0,
              bottom: 0,
              width: '4px',
              cursor: 'col-resize',
              backgroundColor: 'transparent',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.1)',
              },
              '&:active': {
                backgroundColor: 'rgba(0, 0, 0, 0.2)',
              },
              zIndex: 1,
            }}
          />
        </Drawer>
      </Box>
      <Box
        component="main"
        ref={mainContentRef}
        tabIndex={-1}
        sx={mainStyles}
      >
        <Box sx={{
          width: '100%',
          maxWidth: '100%',
          overflowY: 'auto',
          overflowX: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          flex: '1 1 0',
          minHeight: 0,
          height: 0,
        }}>
          {children}
        </Box>
      </Box>
      {/* Logout Confirmation Dialog */}
      <Dialog
        open={logoutDialogOpen}
        onClose={handleLogoutCancel}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            width: { xs: '90%', sm: '400px' },
            m: { xs: 2, sm: 3 }
          }
        }}
      >
        <DialogTitle>Confirm Logout</DialogTitle>
        <DialogContent>
          Are you sure you want to logout?
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleLogoutCancel}>Cancel</Button>
          <Button onClick={handleLogoutConfirm} variant="contained" color="primary">
            Logout
          </Button>
        </DialogActions>
      </Dialog>

      {/* Settings Menu */}
      <Menu
        anchorEl={settingsAnchorEl}
        open={Boolean(settingsAnchorEl)}
        onClose={handleSettingsClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 200,
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            '& .MuiMenuItem-root': {
              py: 1.5,
              px: 2,
            },
          }
        }}
      >
        <MenuItem onClick={handlePasswordChange}>
          <ListItemIcon>
            <Lock fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Change Password" />
        </MenuItem>
        <Divider />
        {sessionStorage.getItem('isSuperAdmin') === 'true' && (
          <MenuItem onClick={handleNetsuiteSetup}>
            <ListItemIcon>
              <Settings fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Netsuite Setup" />
          </MenuItem>
        )}
      </Menu>

      {/* Password Change Dialog */}
      <PasswordChangeDialog
        open={passwordChangeDialogOpen}
        onClose={() => setPasswordChangeDialogOpen(false)}
        username={sessionStorage.getItem('username')}
      />
    </Box>
  );
});

export default DashboardLayout;
